<template>
  <div class="pusher-stats-container">
    <ul
      v-if="pusherUserStatsDataInjected && !isNoData"
      v-infinite-scroll="pusherUserStatsLoadInjected"
      class="infinite-list"
      :class="{ 'infinite-list-mobile': $device.isMobile }"
    >
      <li v-for="(row, rowIndex) in pusherUserStatsDataInjected" :key="rowIndex" class="infinite-list-item">
        <div
          v-for="item in row"
          :key="item.id"
          class="infinite-list-item-card"
          :class="{
            'infinite-list-item-card-active': address?.toLowerCase() === item.userAddress,
            'infinite-list-item-card-mobile': $device.isMobile
          }"
        >
          <span class="greyTitleDate">
            <NuxtTime locale="en-GB" :datetime="item.dateTime" month="numeric" day="numeric" year="numeric" />
          </span>
          <div class="betInfo">
            <img :src="item.userAvatarUrl" alt="avatar" class="avatar" />
            <div class="betInfoContent">
              <span
                class="userName"
                :class="{
                  'ru-locale': isUserNameRuLocale
                }"
              >
                {{ item.userName?.length > USER_NAME_MAX_LENGTH ? formatAddress(item.userName) : item.userName }}
              </span>

              <div class="betInfoRewards">
                <span class="greyTitle">{{ $t('betGotPrefix') }}:</span>
                <span class="tokenValueContainer">
                  <img
                    class="reward-icon"
                    :src="
                      tokens[tokenMetaData(blockchain.contracts, item.tokenAddress).name.contract as Tokens].toString()
                    "
                    width="26"
                    loading="lazy"
                    alt="Pearl Token Icon"
                  />
                  <span class="tokenValue">{{ addThousandSeparators(item.winAmount) }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </li>
    </ul>

    <div v-if="pusherUsersStatsStatusInjected !== AsyncDataStatus.PENDING && isNoData" class="infinite-list-empty">
      {{ $t('coreNoData') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import tokens from 'public/img/tokens';
import type { Tokens } from '~/utils/constants';

import { USER_NAME_MAX_LENGTH } from '~/utils/constants/profile';
import type { PusherUsersStatsItem } from '~/types/Pusher';
import { AsyncDataStatus } from '~/utils/constants/asyncData';
import { addThousandSeparators } from '~/utils/formatters';

const { address } = useWeb3ModalAccount();
const { blockchain } = useEnvs();

const pusherUserStatsLoadInjected = inject<() => void>('pusherUserStatsLoad');
const pusherUserStatsDataInjected = inject<Ref<PusherUsersStatsItem[][]>>('pusherUserStatsData');
const pusherUsersStatsStatusInjected = inject<Ref<AsyncDataStatus>>('pusherUsersStatsStatus');

const isUserNameRuLocale = (name: string) => /^[А-Яа-я]/.test(name);

const isNoData = computed(() => pusherUserStatsDataInjected && pusherUserStatsDataInjected.value.length === 0);
</script>

<style lang="scss" scoped>
.pusher-stats-container {
  height: 100%;
}
.infinite-list {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;
  margin: 0;
  list-style: none;
}

.infinite-list-mobile {
  scrollbar-width: none;
}

.infinite-list .infinite-list-item {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 88px;
  margin-bottom: 12px;

  .infinite-list-item-card {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    flex-direction: column;
    height: 88px;
    background: linear-gradient(to bottom, rgba(78, 49, 39, 1), rgba(0, 0, 0, 1));
    border-radius: 12px;
    padding: 10px 18px 8px;
    width: 259px;
    min-width: 259px;

    .betInfo {
      display: flex;
      gap: 12px;

      .betInfoContent {
        display: flex;
        flex-direction: column;
      }

      .betInfoRewards {
        display: flex;
        align-items: center;
      }
    }

    .avatar {
      height: 49px;
      width: 49px;
      border: 2px solid rgba(32, 26, 14, 1);
      border-radius: 50%;
    }

    .userName {
      font-family: Eczar;
      font-weight: 600;
      font-size: 16px;
      color: var(--dark-7);
      white-space: nowrap;
    }

    .greyTitle {
      font-family: Eczar;
      font-weight: 600;
      font-size: 16px;
      color: var(--dark-7);
    }

    .greyTitleDate {
      font-family: Eczar;
      font-weight: 600;
      font-size: 14px;
      color: var(--dark-7);
      text-align: left;
    }

    .tokenValueContainer {
      display: flex;
      align-items: center;

      .tokenValue {
        font-family: Eczar;
        font-weight: 700;
        font-size: 16px;
        color: var(--main-text-color);
      }
    }
  }

  .infinite-list-item-card-mobile {
    min-width: 100%;
  }

  .infinite-list-item-card-active {
    border: 1px solid rgba(184, 125, 43, 1);
    background: linear-gradient(180deg, rgba(221, 177, 104, 1) 0%, rgba(135, 70, 24, 1) 50%, rgba(12, 4, 5, 1) 100%);
    box-shadow: 0 0 1px 0 #ddcead;

    .greyTitleDate,
    .userName {
      color: var(--main-text-color);
    }
  }
}
.infinite-list .infinite-list-item + .list-item {
  margin-top: 10px;
}

.infinite-list-empty {
  font-size: 24px;
  text-align: center;
  color: var(--dark-8);
}

.avatar-wrapper {
  position: relative;
  width: 75px;

  img {
    width: 32px;
    position: absolute;
    top: 7px;
    right: 7px;
  }

  .avatar {
    border: 2px solid #201a0e;

    &.own {
      border-color: #fff;
    }
  }
}

.eye {
  position: absolute;
  right: 20px;
  top: 15px;
  width: 25px;
  height: 17px;
  cursor: pointer;
}

.date-closed {
  font-family: Eczar;
  font-weight: 600;
  font-size: 14px;
  line-height: 15.4px;
  color: #a2a2a2;
  margin-bottom: 1px;
}

.user-name {
  font-family: Eczar;
  font-weight: 600;
  font-size: 16px;
  line-height: 16px;
  color: #a2a2a2;
  padding-bottom: 7px;
  width: 130px;
  text-overflow: ellipsis;
  overflow-x: hidden;
  white-space: nowrap;

  &.own {
    color: #fff;
  }
}

.user-info {
  display: flex;
  gap: 10px;
}

.user-info.user-right-side {
  margin-top: 16px;
}

.user-info-wrapper {
  display: flex;
  align-items: center;
}

.user-name-block {
  margin-right: 20px;
}

.user-rewards-block {
  display: flex;
  align-items: center;
  gap: 2px;

  .user-rewards-block-label {
    font-family: Eczar;
    font-weight: 600;
    font-size: 16px;
    line-height: 1.1;
    color: rgba(162, 162, 162, 1);
  }
}

.ru-locale {
  font-family: 'PT Serif', sans-serif;
}
</style>
